import { makeApiRequestPromise } from "../../lib/api";
import type { Evaluator, CreateEvaluatorRequest, UpdateEvaluatorRequest } from "./types";

/**
 * Pull the API's own message out of an axios error.
 *
 * The endpoints return meaningful 409s — duplicate criteria, duplicate name — and the
 * UI used to replace all of them with "Failed to update evaluator", which told the user
 * nothing about how to proceed.
 */
const detailOf = (error: any, fallback: string): string =>
  error?.response?.data?.detail || error?.message || fallback;

export const listEvaluators = async (): Promise<Evaluator[]> => {
  try {
    const response = await makeApiRequestPromise({
      method: "GET",
      path: "/api/v1/evaluators",
    });
    if (!Array.isArray(response)) {
      console.error("Unexpected response format from GET /api/v1/evaluators:", response);
      return [];
    }
    return response as Evaluator[];
  } catch (error: any) {
    console.error("Failed to list evaluators:", error);
    throw new Error(detailOf(error, "Failed to load evaluators"));
  }
};

/**
 * Every version of one evaluator, newest first, including soft-deleted ones.
 *
 * `Evaluator.update` is append-only — it soft-deletes the current row and inserts a new
 * one under the same group_id — and this endpoint has always returned that whole chain.
 * Nothing in the UI asked for it, so edits silently redefined the metric behind a chart.
 */
export const listEvaluatorVersions = async (groupId: string): Promise<Evaluator[]> => {
  try {
    const response = await makeApiRequestPromise({
      method: "GET",
      path: "/api/v1/evaluators",
      params: { group_id: groupId },
    });
    if (!Array.isArray(response)) {
      console.error("Unexpected response format from GET /api/v1/evaluators:", response);
      return [];
    }
    return response as Evaluator[];
  } catch (error: any) {
    console.error("Failed to list evaluator versions:", error);
    throw new Error(detailOf(error, "Failed to load version history"));
  }
};

export const createEvaluator = async (
  body: CreateEvaluatorRequest
): Promise<Evaluator> => {
  try {
    const response = await makeApiRequestPromise({
      method: "POST",
      path: "/api/v1/evaluators",
      body,
    });
    return response as Evaluator;
  } catch (error: any) {
    console.error("Failed to create evaluator:", error);
    throw new Error(detailOf(error, "Failed to create evaluator"));
  }
};

export const updateEvaluator = async (
  id: string,
  body: UpdateEvaluatorRequest
): Promise<Evaluator> => {
  try {
    const response = await makeApiRequestPromise({
      method: "PUT",
      path: `/api/v1/evaluators/${id}`,
      body,
    });
    return response as Evaluator;
  } catch (error: any) {
    console.error("Failed to update evaluator:", error);
    throw new Error(detailOf(error, "Failed to update evaluator"));
  }
};

export const deleteEvaluator = async (id: string): Promise<void> => {
  try {
    await makeApiRequestPromise({
      method: "DELETE",
      path: `/api/v1/evaluators/${id}`,
    });
  } catch (error: any) {
    console.error("Failed to delete evaluator:", error);
    throw new Error(detailOf(error, "Failed to delete evaluator"));
  }
};
